import {
    fileIcon,
    LayerPropertiesOptions,
    MeshProperty,
    attributesMap,
    reverseAttributesMap,
    allowedDefaultMultipleAttributes,
    programTranslator,
} from "constants/viewer";
import { ViewerActions, LayersActions, GradientActions, FoldersActions } from "actions";
import ViewerEvents from "events/ViewerEvents";
import * as Utils from "services/UtilsService";
import ApiUtils from "api/Utils";
import { defaultViewProgram } from "constants/viewer";
import { findElement, findItem } from "actions/ViewerTools";

export type IAttribute = {
    index: number;
    label: string;
    name: string;
    unit: string;
    values?: string[];
};

export type ColorLegendType = {
    colorAttribute: string;
    colorAttributeIndex: number;
    colorRangeMin: number;
    colorRangeMax: number;
    colorRangeStep: number;
    colorRangeCount: number;
    colorGradientId: string;
    colorMode: number;
    colorStaticR: number;
    colorStaticG: number;
    colorStaticB: number;
    colorStaticBackR: number;
    colorStaticBackG: number;
    colorStaticBackB: number;
    dataDrivenMethod: number;
    dataDrivenEqualMode: number;
    reverseGradient: boolean;
    interpolateGradient: boolean;
    minMaxDynamic: boolean;
    minMaxData: boolean;
    alphaColorGradientId: string;
    useAttributeGradient: boolean;
    reverseAlphaGradient: boolean;
    attributeGradient: any;
    isAlpha: boolean;
    geoSize: number;
    geoShape: string;
    doubleSided: boolean;
};
export function getLayerNumericProperty(layer: any, property: string, defaultValue: number) {
    if (typeof layer[property] !== "undefined") {
        return parseFloat(layer[property]);
    } else {
        return defaultValue;
    }
}

export function getLayerStringProperty(layer: any, property: string, defaultValue: string) {
    if (typeof layer[property] !== "undefined") {
        return layer[property];
    } else {
        return defaultValue;
    }
}

export function getLayerBooleanProperty(layer: any, property: string, defaultValue: boolean) {
    if (typeof layer[property] !== "undefined") {
        return layer[property] === "1";
    } else {
        return defaultValue;
    }
}

export function getInputName(layerType: string) {
    switch (layerType) {
        case "cylinder":
            return "input_value_dh";
        case "block":
            return "input_value_bm";
        case "surface":
            return "input_value_mesh";
        default:
            return "";
    }
}

export function typeInFilter(item: any, input: any) {
    const { viewer } = ViewerEvents;
    if (!viewer) {
        return;
    }
    return viewer.typeInFilter(item["file_type"], input.filter);
}

export function getFileIcon(file: any) {
    return fileIcon[file.file_type];
}

export function getInputsMesh(layer: any, inputs: any) {
    let meta = inputs[layer.view_type];
    if (meta) {
        const input: any = Object.values(meta).find((input: any) => input.type === "3");
        if (input) {
            const input_id = input.id;
            const input_value = layer["input_value_" + input_id];
            return { id: input_id, value: input_value };
        }
    }
}

export function getLayerInputValue(layer: any, input: any) {
    const id = input["id"];
    const type = parseInt(input["type"]);
    const defaultVal = input["default"] ? parseInt(input["default"]) : 0;
    const value = layer["input_value_" + id];
    if (type === 0 || type === 2) {
        return value ? value : defaultVal;
    } else {
        const { project } = layer;
        const entity = findElement(project, value);
        return entity;
    }
}

export function getFileType(layer: any) {
    var fileType = "FILE";
    if (layer && layer["view_type"]) {
        Object.entries(defaultViewProgram).forEach((entry: any) => {
            if (entry[1] === layer["view_type"]) {
                fileType = entry[0];
            }
        });
    }
    return fileType;
}

export function filterInputTypes(input: any) {
    return input["type"] === "0" || input["type"] === "2" || input["type"] === "3";
}

export function getSourceEntity(layer: any, inputs: any, currentProject: any) {
    if (inputs) {
        let meta = inputs[layer.view_type];
        if (meta) {
            const input: any = Object.values(meta).find((input: any) => input.type === "3");
            //console.log("input:", input);
            if (input) {
                const input_id = input.id;
                const input_value = layer["input_value_" + input_id];
                //console.log("id:", input_id, "|value:", input_value);
                if (input_value) {
                    const file = currentProject[input_value];
                    //console.log("****LayerIcon:", "|" , file);
                    if (file) {
                        return file;
                    }
                }
            }
        }
    }
    return null;
}

export function getLayerType(layer: any, inputs: any, currentProject: any) {
    if (inputs) {
        let meta = inputs[layer.view_type];
        if (meta) {
            const input: any = Object.values(meta).find((input: any) => input.type === "3");
            //console.log("input:", input);
            if (input) {
                const input_id = input.id;
                const input_value = layer["input_value_" + input_id];
                //console.log("id:", input_id, "|value:", input_value);
                if (input_value) {
                    const file = currentProject[input_value];
                    //console.log("****LayerIcon:", "|" , file);
                    if (file) {
                        const fileName = getFileIcon(file);
                        //console.log('fileName:', fileName);
                        return fileName;
                    } else {
                        return input_id;
                    }
                } else {
                    if (input_id === "m" || input_id === "mesh") {
                        return "VOXMESH";
                    }
                    return input_id;
                }
            }
        }
    }
    if (layer["view_type"] === "com.voxelfarm.program.view.planegenerator") {
        return "IDXORTHO";
    }
}

export function getLayerOptions(layerType: string) {
    const translator = { ...programTranslator };
    const ignoreTranslator = typeof translator[layerType] === "undefined";
    if (ignoreTranslator) {
        return LayerPropertiesOptions[layerType];
    } else {
        return LayerPropertiesOptions[translator[layerType]];
    }
}

export function getAttributesObjects(layerId: string) {
    var layersAttributes: any = LayersActions.attributes;
    var attributesObjects: any = layersAttributes[layerId];
    var attributes: any = [];
    if (attributesObjects) {
        attributes = attributesObjects;
    }
    var pending = LayersActions.selectedLayers.length;
    if (pending > 1) {
        var common: any = [];
        LayersActions.selectedLayers.forEach((layer: any) => {
            common = layersAttributes[layer.ID];
            if (attributes.length === 0) {
                attributesObjects = common;
            } else {
                attributesObjects = Utils.findCommonAttributes(attributes, common, "label");
            }
            pending--;
            if (pending === 0 && attributesObjects) {
                attributes = attributesObjects;
            }
        });
    }
    return Array.isArray(attributes) ? attributes : [];
}

export function getAttributes(layerId: string) {
    var layersAttributes: any = LayersActions.attributes;
    var attributesObjects: IAttribute[] = layersAttributes[layerId];
    var attributes: any = [];
    if (attributesObjects) {
        attributes = attributesObjects.map((attributeObject: any) => attributeObject.name);
    }
    var pending = LayersActions.selectedLayers.length;
    if (pending > 1) {
        var common: any = [];
        LayersActions.selectedLayers.forEach((layer: any) => {
            common = layersAttributes[layer.ID];
            if (attributes.length === 0) {
                attributesObjects = common;
            } else {
                attributesObjects = Utils.findCommonAttributes(attributes, common, "name");
            }
            pending--;
            if (pending === 0 && attributesObjects) {
                attributes = attributesObjects.map((attributeObject: any) => attributeObject.name);
            }
        });
    }

    if (attributes.length === 0) attributes.push("Default");
    return attributes;
}

export function getAttributesValues(layerId: string) {
    var layersAttributes: any = LayersActions.attributes;
    var attributesObjects: IAttribute[] = layersAttributes[layerId];
    var values: any = [];
    if (attributesObjects) {
        attributesObjects.forEach((attributeObject: IAttribute) => {
            if (attributeObject.values) {
                values.push(attributeObject);
            }
        });
    }
    return values;
}

export function getEntityAttributes(entityList: string, project: any) {
    var attributes: any = [];
    var getAttributes = (entityId: string, project: any) => {
        var attributes: any = [];
        forEachEntityAttribute(entityId, project, (attribute: any) => {
            attributes.push(attribute.name);
        });
        return attributes;
    };

    var list = entityList.split(" ");
    var pending = list.length;
    var common: any = [];
    if (pending > 1) {
        list.forEach((entityList: string) => {
            common = getAttributes(entityList, project);
            if (attributes.length === 0) {
                attributes = common;
            } else {
                attributes = Utils.findCommonValues(attributes, common);
            }
            pending--;
            if (pending === 0) {
                return attributes;
            }
        });
    } else {
        attributes = getAttributes(entityList, project);
    }
    return attributes;
}

export function getAttributeIndex(attributeName: string, layer: any) {
    let attributes: any = getAttributes(layer.ID);
    return attributes.findIndex((attribute: string) => attribute === attributeName);
}

export function forEachEntityAttribute(entityId: string, project: any, fn: any) {
    let entity: any = project[entityId];
    if (entity) {
        if (entity.hasOwnProperty("attribute_count")) {
            // has attributes as properties
            const attributeCount: number = entity["attribute_count"];
            let abort: boolean = false;
            for (let attributeIndex: number = 0; attributeIndex < attributeCount && !abort; ++attributeIndex) {
                const key_name: string = "attribute" + attributeIndex + "_name";
                abort =
                    false ===
                    fn({
                        name: entity[key_name],
                        index: attributeIndex,
                    });
            }
        } else if (entity.hasOwnProperty("source")) {
            const sourceId: string = entity["source"];
            let sourceEntity: any = project[sourceId];
            if (sourceEntity && sourceEntity.hasOwnProperty("column_meta")) {
                let columnMeta = atob(sourceEntity["column_meta"]);
                let attributeTokens = columnMeta.split(",");
                let abort: boolean = false;
                for (let i: number = 0; i < attributeTokens.length && !abort; i++) {
                    let tokens: any = attributeTokens[i].split(" ");
                    if (tokens.length < 2 || tokens[0].length === 0 || tokens[1] !== "VALUE") continue;
                    abort =
                        false ===
                        fn({
                            name: tokens[0],
                            index: i,
                        });
                }
            }
        }
    }
}

export const findSelectedObjects = (allObjects: any, selectedEntities: any) => {
    const renderedObjects: any = [];
    Object.entries(allObjects).forEach((entries: any) => {
        const workflow_id = entries[0];
        const objects = entries[1];
        Object.values(objects).forEach((arr: any) => {
            const selected = arr.find(
                (el: any) => selectedEntities[workflow_id] && el["ID"] === selectedEntities[workflow_id]["ID"]
            );
            if (selected) {
                renderedObjects.push(selected);
            } else {
                renderedObjects.push(Utils.maxValueArrayProp(arr, "file_date"));
            }
        });
    });
    return renderedObjects;
};

export const getWorkflowsVersions = (objects: any) => {
    const allObjects: any = {};
    objects.forEach((object: any) => {
        const name = object["name"];
        const workflow = FoldersActions.getEntityWorkflow(object) || "unversioned";
        //console.log("name:", name, "|workflow:", workflow);

        if (!allObjects[workflow]) {
            allObjects[workflow] = {};
        }

        if (!allObjects[workflow][name]) {
            allObjects[workflow][name] = [];
        }

        allObjects[workflow][name].push(object);
        allObjects[workflow][name] = Utils.sortArrayByProp(allObjects[workflow][name], "file_date", true);
    });
    return allObjects;
};

export const getLayerIndex = (entityId: string) => {
    var index: number = -1;
    if (ViewerEvents.viewer) {
        var { compositeMeta } = ViewerEvents;
        if (compositeMeta) {
            compositeMeta.components.forEach((component: any, idx: number) => {
                if (component.type === 7 && component.id === entityId) {
                    index = idx;
                } else if (component.layerStack) {
                    component.layerStack.forEach((layer: any) => {
                        if (layer.id === entityId) {
                            index = idx;
                        }
                    });
                } else if (component.source === entityId) {
                    index = idx;
                }
            });
        }
    }
    return index;
};

export function getEntityId(layer: any, inputs?: any) {
    const viewType = layer["view_type"];
    if (!inputs) {
        inputs = ViewerEvents.inputs;
    }

    if (!inputs || Object.keys(inputs).length === 0) {
        let result: any = getLayerInputs(layer);
        if (result[viewType]) {
            inputs = result;
        } else {
            inputs = {};
            inputs[viewType] = result;
        }
    }

    console.log("getEntityId.inputs:", inputs);

    let entityId = "";

    if (viewType === defaultViewProgram["IDXORTHO"]) {
        entityId = layer["ortho_list"];
    } else {
        if (inputs && inputs[viewType]) {
            const arr = Object.values(inputs[viewType]).filter((elem: any) => elem.type === "3");
            arr.forEach((input: any) => {
                var inputId = input["id"];
                if (layer["input_value_" + inputId]) {
                    entityId = layer["input_value_" + inputId];
                }
            });
        }
        if (layer["input_value_singleton"]) {
            entityId = layer["input_value_singleton"];
        }
    }
    return entityId;
}

export function getComponentId(layerIndex: number) {
    const { compositeMeta } = ViewerEvents;
    const getComponent = (component: any): string => {
        if (component === null || typeof component === "undefined") {
            return "";
        }

        if (typeof component.id !== "undefined") {
            return component.id;
        } else if (typeof component.layer !== "undefined") {
            if (component.layer.id) {
                return component.layer.id;
            }
        } else if (typeof component.layerStack !== "undefined") {
            return component.layerStack.map((layer: any) => layer.id).join("");
        }
        if (typeof component.opA !== "undefined" && component.opA !== null) {
            return getComponent(component.opA);
        }
        if (typeof component.opB !== "undefined" && component.opB !== null && component.opB.id) {
            return getComponent(component.opB);
        }
        return "";
    };

    const component = compositeMeta.components[layerIndex];
    console.log("getComponentId:", component);
    return getComponent(component);
}

export function getEntitiesId(layer: any, inputs: any, viewType: string, type: string) {
    var arr: any = [],
        entities: any = [];
    if (inputs && inputs[viewType]) {
        arr = Object.values(inputs[viewType]).filter((elem: any) => elem.type === type);
        arr.push({ id: "singleton" });
        arr.forEach((input: any) => {
            var inputId: string = input["id"];
            let value = layer["input_value_" + inputId];
            if (!inputId || (!value && inputId !== "singleton")) {
                if (type === "3" && input.entity !== "-1") {
                    inputId = inputs[viewType][input.entity].id;
                } else if (type === "5" && input.entity !== "-1") {
                    inputId = inputs[viewType][input.entity].id;
                } else if (type === "8" && input.input_ref_id) {
                    inputId = inputs[viewType][input.input_ref_id].id;
                }
                value = layer["input_value_" + inputId];
            }

            entities.push(value);
        });
    }
    entities = entities.filter((entity: any) => entity !== undefined && entity !== "");
    return entities;
}

export function getInputsFromLayer(layer: any) {
    console.log("getInputsFromLayer.layer:", layer);
    let props = Object.getOwnPropertyNames(layer);
    console.log("===>517.props:", props);
    props = props.filter((prop: string) => prop.includes("input_type"));
    console.log("===>519.props:", props);
    const view_type: string = layer["view_type"];
    const inputs: any = {};
    const inputTypes: any = [];
    props.forEach((prop: string) => {
        if (layer[prop] === "3") {
            const id = prop.replace("input_type_", "");
            inputTypes.push({ id, type: 3 });
        }
    });
    inputs[view_type] = inputTypes;
    return inputs;
}

export function getLayerInputs(layer: any) {
    let inputs: any = {};
    const view_type: string = layer["view_type"];
    if (ViewerEvents.inputs && ViewerEvents.inputs[view_type]) {
        return ViewerEvents.inputs;
    }
    //if (inputs[view_type].length > 0) {
    //    return inputs;
    //}
    //inputs[view_type] = [];
    const entries: any = [];
    Object.keys(layer).forEach((input) => {
        const entry: any = { editor: "0", entity: "0" };
        if (input.includes("input_type_")) {
            var id = input.replace("input_type_", "");
            entry["id"] = id;
            if (layer["input_type_" + id]) {
                var type = layer["input_type_" + id];
                entry["type"] = type;
            }
            if (layer["input_label_" + id]) {
                var label = layer["input_label_" + id];
                entry["label"] = label;
            }
            if (layer["input_filter_" + id]) {
                var filter = layer["input_filter_" + id];
                entry["filter"] = filter;
            }
            if (id && id !== "undefined") {
                entries.push(entry);
            }
        }
    });
    inputs[view_type] = entries;
    return inputs;
}

export function getLayersInputs(layers: any) {
    let inputs: any = [];
    Object.values(layers).forEach((layer: any) => {
        if (layer && layer["view_type"]) {
            inputs = inputs.concat(getLayerInputs(layer));
        }
    });
    return inputs;
}

export function getDataMinMaxValue(data: any, index: number) {
    var min: string = "0";
    var minOk: boolean = false;
    var max: string = "1";
    var maxOk: boolean = false;

    if (data) {
        if (typeof data["attribute" + index + "_bound_min"] !== "undefined") {
            min = data["attribute" + index + "_bound_min"];
            minOk = true;
        }
        if (typeof data["attribute" + index + "_bound_max"] !== "undefined") {
            max = data["attribute" + index + "_bound_max"];
            maxOk = true;
        }
    }
    return {
        min: min,
        minOk: minOk,
        max: max,
        maxOk: maxOk,
    };
}

const getExtendedMeta = (projectId: string, entitiesList: any, done: any) => {
    const promises: any = [];
    console.log("gettingExtendMeta...");

    const extendedMeta: any = {};
    const remoteList: any = [];
    Object.entries(entitiesList).forEach((entry) => {
        const layerId: string = entry[0];
        const curList: any = entry[1];
        curList.forEach((entityId: string) => {
            if (
                !ViewerActions.extendedMeta[entityId] &&
                !remoteList.includes(entityId) &&
                ViewerActions.extendedMeta[entityId] !== null
            ) {
                let promise = ApiUtils.fetchAttributes(projectId, layerId, entityId);
                promises.push(promise);
                remoteList.push(entityId);
            } else {
                if (!extendedMeta[layerId]) {
                    extendedMeta[layerId] = {};
                }
                extendedMeta[layerId][entityId] = ViewerActions.extendedMeta[entityId];
            }
        });
    });

    Promise.allSettled(promises).then((results) => {
        console.log("results:", results);
        results.forEach((results: any) => {
            const { value } = results;
            const { layerId, data, entityId } = value;
            if (!extendedMeta[layerId]) {
                extendedMeta[layerId] = {};
            }
            if (data && data !== null && data !== "") {
                const config_ini = Utils.parseINIString(data);
                const params = config_ini["Configuration"];
                extendedMeta[layerId][entityId] = params;
                ViewerActions.extendedMeta[entityId] = params;
                const entity = findItem(entityId);
                // Exception for the singleton extended meta
                if (entity && entity["file_type"] === "RAW") {
                    ViewerActions.viewer.setExtendedMeta(0, params, entityId);
                } else {
                    const index = getLayerIndex(entityId);
                    if (index > -1 && ViewerActions.viewer) {
                        console.log(
                            `%cSetting extendedMeta for ${index} component with ${entityId}`,
                            "color:green",
                            params
                        );
                        ViewerActions.viewer.setExtendedMeta(index, params, entityId);
                    }
                }
                if (!LayersActions.data[layerId]) {
                    LayersActions.data[layerId] = {};
                    LayersActions.data[layerId] = params;
                }
            } else {
                extendedMeta[layerId][entityId] = null;
                ViewerActions.extendedMeta[entityId] = null;
                if (ViewerActions.viewer) {
                    ViewerActions.viewer.setExtendedMeta(-1, null, entityId);
                }
            }
        });
        if (done) {
            console.log("ViewerActions.extendedMeta:", ViewerActions.extendedMeta);
            done(extendedMeta);
        }
    });
};

export function getEntityAttributesList(entitiesAttributes: any, callback: any) {
    var project = ViewerEvents.currentProject;
    if (!project) {
        return;
    }
    const projectId = ViewerEvents.projectId;
    var pending = 0;
    Object.values(entitiesAttributes).forEach((entry: any) => (pending += entry.length));
    var attributes: any = [];
    var entityAttributes: any = [];

    var processData = (layerId: string, entityId: string, meta: any, fn: any) => {
        if (!attributes[layerId] || attributes[layerId] === null) {
            attributes[layerId] = [];
        }
        if ((entityId && !entityAttributes[entityId]) || entityAttributes[entityId] === null) {
            entityAttributes[entityId] = [];
        }
        console.log("processData:", meta);
        if (meta) {
            if (meta.hasOwnProperty("attribute_count")) {
                const attributeCount: number = meta["attribute_count"];
                let abort: boolean = false;
                for (let attributeIndex: number = 0; attributeIndex < attributeCount && !abort; ++attributeIndex) {
                    const type_string: string = "attribute" + attributeIndex + "_type_string";
                    const key_set_count: string = "attribute" + attributeIndex + "_set_count";
                    const key_name: string = "attribute" + attributeIndex + "_name";
                    const key_label: string = "attribute" + attributeIndex + "_label";
                    const key_unit: string = "attribute" + attributeIndex + "_unit";

                    if (meta[type_string] !== "string") {
                        attributes[layerId].push({
                            name: meta[key_name],
                            label: meta[key_label] || meta[key_name],
                            unit: meta[key_unit],
                            index: attributeIndex,
                        });
                        entityAttributes[entityId].push({
                            name: meta[key_name],
                            label: meta[key_label] || meta[key_name],
                            unit: meta[key_unit],
                            index: attributeIndex,
                        });
                    } else {
                        attributes[layerId].push({
                            name: meta[key_name],
                            label: meta[key_label] || meta[key_name],
                            unit: meta[key_unit],
                            index: attributeIndex,
                            values: [],
                        });
                        entityAttributes[entityId].push({
                            name: meta[key_name],
                            label: meta[key_label] || meta[key_name],
                            unit: meta[key_unit],
                            index: attributeIndex,
                            values: [],
                        });
                        const setValueCount = meta[key_set_count];
                        for (var i = 0; i < parseInt(setValueCount); i++) {
                            const value_string: string = "attribute" + attributeIndex + "_set_value" + i;
                            const value: string = meta[value_string];
                            attributes[layerId][attributeIndex].values.push(value);
                            entityAttributes[entityId][attributeIndex].values.push(value);
                        }
                    }
                }
            } else if (meta.hasOwnProperty("source")) {
                const entityId: string = meta["ID"];
                const sourceId: string = meta["source"];
                let sourceEntity: any = project[sourceId];
                if (sourceEntity && sourceEntity.hasOwnProperty("column_meta")) {
                    let columnMeta = atob(sourceEntity["column_meta"]);
                    let attributeTokens = columnMeta.split(",");
                    let abort: boolean = false;
                    for (let i: number = 0; i < attributeTokens.length && !abort; i++) {
                        let tokens: any = attributeTokens[i].split(" ");
                        if (tokens.length < 2 || tokens[0].length === 0 || tokens[1] !== "VALUE") continue;
                        abort =
                            false ===
                            attributes[layerId].push({
                                name: tokens[0],
                                label: tokens[0],
                                index: i,
                                unit: "N/A",
                            });
                        entityAttributes[entityId].push({
                            name: tokens[0],
                            label: tokens[0],
                            index: i,
                            unit: "N/A",
                        });
                    }
                }
            }
        }
        pending--;
        if (pending === 0) {
            LayersActions.entitiesData = entityAttributes;
            fn(attributes);
        }
    };
    getExtendedMeta(projectId, entitiesAttributes, (extendedMeta: any) => {
        Object.entries(extendedMeta).forEach((entry: any) => {
            const layerId: string = entry[0];
            const entitiesList: any = entry[1];
            Object.entries(entitiesList).forEach((entityEntry: any) => {
                const entityId = entityEntry[0];
                const meta = entityEntry[1];
                if (meta === null) {
                    const entity = findElement(projectId, entityId);
                    processData(layerId, entityId, entity, callback);
                } else {
                    processData(layerId, entityId, meta, callback);
                }
            });
        });
    });
}

export function getEntityBounds(idsList: any, currentProject: any, callback: any) {
    var minX, minY, minZ, maxX, maxY, maxZ;
    var box = {
        min: { x: Number.MAX_VALUE, y: Number.MAX_VALUE, z: Number.MAX_VALUE },
        max: { x: -Number.MAX_VALUE, y: -Number.MAX_VALUE, z: -Number.MAX_VALUE },
    };
    var boundsFound = false;
    idsList.forEach((entityId: string) => {
        let entity: any = currentProject[entityId];
        if (entity && entity["aabb_min_x"]) {
            boundsFound = true;
            minX = parseFloat(entity["aabb_min_x"]);
            minY = parseFloat(entity["aabb_min_y"]);
            minZ = parseFloat(entity["aabb_min_z"]);
            maxX = parseFloat(entity["aabb_max_x"]);
            maxY = parseFloat(entity["aabb_max_y"]);
            maxZ = parseFloat(entity["aabb_max_z"]);

            box.min.x = Math.min(minX, box.min.x);
            box.min.y = Math.min(minY, box.min.y);
            box.min.z = Math.min(minZ, box.min.z);

            box.max.x = Math.max(maxX, box.max.x);
            box.max.y = Math.max(maxY, box.max.y);
            box.max.z = Math.max(maxZ, box.max.z);
        }
    });
    if (boundsFound) {
        if (box.max.x - box.min.x < Number.EPSILON) {
            box.max.x += 1.0;
        }
        if (box.max.y - box.min.y < Number.EPSILON) {
            box.max.y += 1.0;
        }
        if (box.max.z - box.min.z < Number.EPSILON) {
            box.max.z += 1.0;
        }
        callback(box);
    }
}

export function getEntitiesBounds(idsList: any, currentProject: any) {
    var minX, minY, minZ, maxX, maxY, maxZ;
    var box = {
        min: { x: Number.MAX_VALUE, y: Number.MAX_VALUE, z: Number.MAX_VALUE },
        max: { x: -Number.MAX_VALUE, y: -Number.MAX_VALUE, z: -Number.MAX_VALUE },
    };
    idsList.forEach((entityId: string) => {
        let entity: any = currentProject[entityId];
        if (entity && entity["aabb_min_x"]) {
            minX = parseFloat(entity["aabb_min_x"]);
            minY = parseFloat(entity["aabb_min_y"]);
            minZ = parseFloat(entity["aabb_min_z"]);
            maxX = parseFloat(entity["aabb_max_x"]);
            maxY = parseFloat(entity["aabb_max_y"]);
            maxZ = parseFloat(entity["aabb_max_z"]);

            box.min.x = Math.min(minX, box.min.x);
            box.min.y = Math.min(minY, box.min.y);
            box.min.z = Math.min(minZ, box.min.z);

            box.max.x = Math.max(maxX, box.max.x);
            box.max.y = Math.max(maxY, box.max.y);
            box.max.z = Math.max(maxZ, box.max.z);
        }
    });
    return box;
}

const ColorLegendDefault: any = {
    defaultGradientAttribute: "",
    defaultGradientMin: 0.0,
    defaultGradientMax: 100.0,
    defaultGradientStep: 1.0,
    defaultGradientCount: 10,
    defaultGradientId: "color",
    defaultAlphaGradientId: "color",
    defaultMinMaxDynamic: false,
    defaultMinMaxData: false,
    defaultReverseGradient: false,
    defaultReverseAlphaGradient: false,
    defaultInterpolateGradient: false,
    defaultIsAlpha: false,
};

export function getColorLegendSettings(layer: any, colorLegend?: any) {
    if (!colorLegend) {
        colorLegend = { ...ColorLegendDefault };
    }
    const settings: any = {
        id: layer.ID,
        colorAttribute: getLayerStringProperty(layer, "color_legend_attribute", colorLegend.defaultGradientAttribute),
        colorAttributeIndex: getLayerNumericProperty(layer, "color_legend_attribute_index", -1),
        colorRangeMin: getLayerNumericProperty(layer, "color_legend_range_min", colorLegend.defaultGradientMin),
        colorRangeMax: getLayerNumericProperty(layer, "color_legend_range_max", colorLegend.defaultGradientMax),
        colorRangeStep: getLayerNumericProperty(layer, "color_legend_range_step", colorLegend.defaultGradientStep),
        colorRangeCount: getLayerNumericProperty(layer, "color_legend_range_count", colorLegend.defaultGradientCount),
        colorGradientId: getLayerStringProperty(layer, "color_legend_gradient", colorLegend.defaultGradientId),
        alphaColorGradientId: getLayerStringProperty(
            layer,
            "color_legend_alpha_gradient",
            colorLegend.defaultAlphaGradientId
        ),
        colorMode: getLayerNumericProperty(layer, "color_legend_mode", 2),
        colorStaticR: getLayerNumericProperty(layer, "color_legend_static_r", 128),
        colorStaticG: getLayerNumericProperty(layer, "color_legend_static_g", 128),
        colorStaticB: getLayerNumericProperty(layer, "color_legend_static_b", 128),
        colorStaticBackR: getLayerNumericProperty(layer, "color_legend_static_back_r", 128),
        colorStaticBackG: getLayerNumericProperty(layer, "color_legend_static_back_g", 128),
        colorStaticBackB: getLayerNumericProperty(layer, "color_legend_static_back_b", 128),
        doubleSided: getLayerBooleanProperty(layer, "color_legend_double_sided", false),
        dataDrivenMethod: getLayerNumericProperty(layer, "color_legend_data_driven_method", 0),
        dataDrivenEqualMode: getLayerNumericProperty(layer, "color_legend_data_driven_equal_mode", 0),
        minMaxDynamic: getLayerBooleanProperty(
            layer,
            "color_legend_data_driven_equal_minmax_dynamic",
            colorLegend.defaultMinMaxDynamic
        ),
        minMaxData: getLayerBooleanProperty(
            layer,
            "color_legend_data_driven_equal_minmax_data",
            colorLegend.defaultMinMaxData
        ),
        reverseGradient: getLayerBooleanProperty(
            layer,
            "color_legend_reverse_gradient",
            colorLegend.defaultReverseGradient
        ),
        reverseAlphaGradient: getLayerBooleanProperty(
            layer,
            "color_legend_reverse_alpha_gradient",
            colorLegend.defaultReverseAlphaGradient
        ),
        interpolateGradient: getLayerBooleanProperty(
            layer,
            "color_legend_interpolate_gradient",
            colorLegend.defaultInterpolateGradient
        ),
        attributeQueryMode: getLayerNumericProperty(layer, "attribute_query_mode", 1),
        attributeQueryString: getLayerStringProperty(layer, "attribute_query_string", ""),
        attributeQueryBuilderValue: getLayerStringProperty(layer, "attribute_query_builder", ""),
        isAlpha: getLayerBooleanProperty(layer, "alpha_attribute", colorLegend.defaultIsAlpha),
        useAttributeGradient: false,
        attributeGradient: [],
    };

    if (settings.alphaColorGradientId === "custom" || settings.isAlpha) {
        settings.useAttributeGradient = true;
    }

    if (settings.isAlpha && settings.colorAttribute && settings.colorAttribute !== "") {
        const attributes = getAttributesObjects(layer.ID);
        const attribute = attributes.find((attribute: any) => attribute.name === settings.colorAttribute);
        var values = [];
        if (attribute && attribute.values) {
            values = attribute.values;
        }
        const gradient = GradientActions.getAlphaGradient(layer, values);
        settings.attributeGradient = Utils.rgbToHSL(gradient);
    }

    return settings;
}

export function getDrillHoleSettings(layer: any) {
    return {
        //Layer id
        id: layer.ID,

        //Vector visibilities
        showCollars: getLayerNumericProperty(layer, "drillholes_show_collars", 1) === 1,
        showCollarLabels: getLayerNumericProperty(layer, "drillholes_show_collar_labels", 1) === 1,
        showCylinders: getLayerNumericProperty(layer, "drillholes_show_cylinders", 1) === 1,
        showAttributes: getLayerNumericProperty(layer, "drillholes_show_attributes", 1) === 1,

        //Strip attributes to display
        attributes: getLayerStringProperty(layer, "drillholes_attributes", ""),

        //Attributes text size
        attributesTextSize: getLayerNumericProperty(layer, "drillholes_attributes_text_size", 1),

        //Collar legend
        collarColour: getLayerStringProperty(layer, "collar_legend_color", "#ffffff"),
        collarSize: getLayerNumericProperty(layer, "collar_legend_radius", 10.0),
        collarTextSize: getLayerNumericProperty(layer, "collar_legend_text_size", 1.0),

        //Tube legend
        scaleMode: getLayerNumericProperty(layer, "tube_legend_scale_mode", 1),
        cylinderRadius: getLayerNumericProperty(layer, "tube_legend_radius", 10.0),
        radiusAttributes: getLayerStringProperty(layer, "tube_legend_attribute", ""),
        radiusAttributeIndex: getLayerNumericProperty(layer, "tube_legend_attribute_index", 0),
        radiusAttributeRangeMin: getLayerNumericProperty(layer, "tube_legend_range_min", 0.0),
        radiusAttributeRangeMax: getLayerNumericProperty(layer, "tube_legend_range_max", 100.0),
        radiusAttributeRangeStep: getLayerNumericProperty(layer, "tube_legend_range_step", 1.0),
        radiusRangeMin: getLayerNumericProperty(layer, "tube_legend_radius_min", 1.0),
        radiusRangeMax: getLayerNumericProperty(layer, "tube_legend_radius_max", 100.0),

        //GeoChem
        geoShowZ: getLayerNumericProperty(layer, "geo_chem_show_z", 1) === 1,
        geoShowLabels: getLayerNumericProperty(layer, "geo_chem_show_labels", 1) === 1,
        geoLabelSize: getLayerNumericProperty(layer, "geo_chem_label_size", 1.0),
        geoShowElevation: getLayerNumericProperty(layer, "geo_chem_show_elevation", 1) === 1,
        geoPlaneValue: getLayerNumericProperty(layer, "geo_chem_plane_value", 1),
        geoAttributes: {
            top: {
                attribute: getLayerStringProperty(layer, "geo_chem_top_attribute", "Label"),
                color: getLayerStringProperty(layer, "geo_chem_top_color", "#FFFFFF"),
                index: getLayerNumericProperty(layer, "geo_chem_top_index", -2),
            },
            bottom: {
                attribute: getLayerStringProperty(layer, "geo_chem_bottom_attribute", ""),
                color: getLayerStringProperty(layer, "geo_chem_bottom_color", "#283C50"),
                index: getLayerNumericProperty(layer, "geo_chem_bottom_index", -1),
            },
            left: {
                attribute: getLayerStringProperty(layer, "geo_chem_left_attribute", ""),
                color: getLayerStringProperty(layer, "geo_chem_left_color", "#D00808"),
                index: getLayerNumericProperty(layer, "geo_chem_left_index", -1),
            },
            right: {
                attribute: getLayerStringProperty(layer, "geo_chem_right_attribute", ""),
                color: getLayerStringProperty(layer, "geo_chem_right_color", "#EB7200"),
                index: getLayerNumericProperty(layer, "geo_chem_right_index", -1),
            },
        },
        geoSize: getLayerNumericProperty(layer, "geo_chem_size", 1.0),
        geoShape: getLayerNumericProperty(layer, "geo_chem_shape", 0),
    };
}

export function getLayerSettings(layer: any, attributes: any, defaultValues: any = []) {
    var values: any = {};
    attributes.forEach((attribute: string, index: number) => {
        var name = Utils.slugToCamelCase(attribute);
        if (layer[attribute]) {
            if (typeof defaultValues[index] === "number") {
                values[name] = parseInt(layer[attribute]);
            } else if (typeof defaultValues[index] === "boolean") {
                values[name] = layer[attribute] === "1";
            } else {
                values[name] = layer[attribute];
            }
        } else if (typeof defaultValues[index] !== "undefined") {
            values[name] = defaultValues[index];
        } else {
            values[name] = undefined;
        }
    });
    return values;
}

export const updateMeshProperties = (layer: any, meshProperty: MeshProperty, callback?: any) => {
    if (ViewerEvents.viewer && layer.parent_layer) {
        ViewerActions.setMeshProperties(layer, meshProperty, true);
    }
    if (callback) {
        callback();
    }
};

export const getMeshProperties = (layer: any) => {
    let r = 0.5;
    let g = 0.5;
    let b = 0.5;
    if (layer.color_legend_static_r !== null) {
        r = layer.color_legend_static_r ? layer.color_legend_static_r / 255.0 : r;
        g = layer.color_legend_static_g ? layer.color_legend_static_g / 255.0 : g;
        b = layer.color_legend_static_b ? layer.color_legend_static_b / 255.0 : b;
    }
    let back_r = 0.5;
    let back_g = 0.5;
    let back_b = 0.5;
    if (layer.color_legend_static_back_r !== null) {
        back_r = layer.color_legend_static_back_r ? layer.color_legend_static_back_r / 255.0 : back_r;
        back_g = layer.color_legend_static_back_g ? layer.color_legend_static_back_g / 255.0 : back_g;
        back_b = layer.color_legend_static_back_b ? layer.color_legend_static_back_b / 255.0 : back_b;
    }

    var meshProperties = {
        parentLayer: layer.parent_layer,
        meshIndex: layer.index,
        meshVisible: layer.layer_visible === "1",
        meshOpacity: layer.layer_opacity ? parseFloat(layer.layer_opacity) : 0,
        meshUseParentColorLegend: layer.use_parent === "1",
        meshStaticColorFront: [r, g, b],
        meshStaticColorBack: [back_r, back_g, back_b],
    };
    return meshProperties;
};

export function getGlobalSettings(layer: any) {
    var settings: any = {};
    allowedDefaultMultipleAttributes.forEach((prop: string) => {
        var attributeName = attributesMap[prop];
        if (!layer[attributeName]) {
            settings[prop] = ColorLegendDefault[prop]; //Might not be working because ColorLegendDefault property names starts with default*
        }
    });
    Object.getOwnPropertyNames(layer).forEach((attributeName: string) => {
        var prop = reverseAttributesMap[attributeName];
        if (prop && !settings[prop.name]) {
            settings[prop.name] = prop.type === "string" ? layer[attributeName] : parseFloat(layer[attributeName]);
        }
    });
    return settings;
}

export function getCommonAttributeValue(selectedLayers: any) {
    var filteredLayers: any = [];
    function filterAttributes(layer: any) {
        var filteredLayer: any = [];
        Object.getOwnPropertyNames(layer).forEach((attribute: string) => {
            if (
                attribute.includes("color_legend") ||
                attribute.includes("attribute_query") ||
                attribute.includes("drillholes_") ||
                attribute.includes("tube_legend_") ||
                attribute.includes("use_parent") ||
                attribute.includes("not_selectable") ||
                attribute.includes("parent_layer")
            ) {
                filteredLayer[attribute] = layer[attribute];
            }
        });
        return filteredLayer;
    }
    selectedLayers.forEach((layer: any) => {
        filteredLayers.push(filterAttributes(layer));
    });

    var obj: any = {};
    var output: any = {};

    filteredLayers.forEach((layer: any, index: number) => {
        Object.getOwnPropertyNames(layer).forEach((attribute: string) => {
            if (attribute !== "length") {
                if (index === 0) {
                    if (!obj.hasOwnProperty(attribute)) {
                        obj[attribute] = layer[attribute];
                    }
                } else {
                    if (!layer.hasOwnProperty(attribute)) {
                        delete obj[attribute];
                    } else if (obj.hasOwnProperty(attribute)) {
                        if (obj[attribute] !== layer[attribute]) {
                            delete obj[attribute];
                            delete output[attribute];
                        } else {
                            //console.log('add.attribute:', attribute, ':', obj[attribute]);
                            output[attribute] = obj[attribute];
                        }
                    }
                }
            }
        });
    });
    //console.log('***result=', output);
    return output;
}

export var isLimboState = (selectedLayers: any, attribute: string) => {
    var values: any = [];
    var prevVal: any;
    var isLimbo: boolean = false;

    selectedLayers.forEach((layer: any) => {
        values[layer.ID] = layer[attribute];
    });
    Object.values(values).forEach((value: any, index: number) => {
        if (index === 0) {
            prevVal = value;
        } else {
            if (prevVal !== value) {
                isLimbo = true;
                return;
            } else {
                prevVal = value;
            }
        }
    });
    //console.log('selectedLayers', selectedLayers);
    //console.log('matches:', matches, '|isLimbo:', isLimbo);
    return isLimbo;
};

export function checkBlockModel(layer: any) {
    let isBlockModel = false;
    switch (layer["view_type"]) {
        case defaultViewProgram.VOXBM:
        case defaultViewProgram.VOXMESH:
            isBlockModel = true;
            break;
    }
    return isBlockModel;
}
export function requiresServerUpdate(attributeQueryEntity: string, currentProject: any) {
    return true;
    /*
	let entity:any = currentProject[attributeQueryEntity];
	if (entity) {
		if (entity["file_type"] === "VOXBT" || entity["file_type"] === "VOXBM") {
			return true;
		}
	}
	return false;
	*/
}

export function getPlaneInputValues(planeValue: number) {
    var a: number = 0,
        b: number = 0,
        c: number = 1,
        d: number = -planeValue;

    return { a: a.toFixed(1), b: b.toFixed(1), c: c.toFixed(1), d: d.toFixed(1) };
}
